import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import SelectField from '../common/SelectField.js';
import Loader from '../common/Loader.js';
import { DashboardLayout } from '../common/page_component/index.js';
import { paymentService } from '../../services/ShipmentPaymentService.js';
import { shipmentService } from '../../services/shipmentService.js';
import Alert from '../common/Alert.js';
import { Truck, Pencil, ArrowLeft, MapPin} from "lucide-react";
import { validators, useValidation, ValidationMessage } from '../common/Validation.js';
import Button from '../common/Button.js';
import { useTranslation } from 'react-i18next';


export default function EditPayment() {
  const navigate = useNavigate();
  const { paymentId } = useParams();
  const { errors, validate, validateField } = useValidation();

  const validationRules = {
    payment_amount: [(v) => validators.required(v, 'Payment amount'), (v) => validators.number(v, 0.01)],
    payment_mode:   (v) => validators.select(v, 'payment mode'),
  };

  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(true);
  const [shipmentDetails, setShipmentDetails] = useState(null);
  const [originalPayment, setOriginalPayment] = useState(null);
  const [formData, setFormData] = useState({
    payment_amount: '',
    payment_mode: 'cash',
    remarks: ''
  });
  const [existingReceipt, setExistingReceipt] = useState(null);

  const paymentModes = [
    { value: 'cash', label: 'CASH' },
    { value: 'upi', label: 'UPI' },
  ];

  useEffect(() => {
    fetchPaymentData();
  }, [paymentId]);

  const fetchPaymentData = async () => {
    try {
      setFetchingData(true);
      const response = await paymentService.getPaymentById(paymentId);
      const payment = response.data.data;
      setOriginalPayment(payment);
      setFormData({
        payment_amount: payment.payment_amount || '',
        payment_mode: payment.payment_mode || 'cash',
        remarks: payment.remarks || ''
      });
      if (payment.has_receipt && payment.receipt_url) {
        setExistingReceipt(payment.receipt_url);
      }

      // Fetch shipment details
      const shipmentRes = await shipmentService.getShipment(payment.shipment_id);
      setShipmentDetails(shipmentRes.data.shipment || shipmentRes.data.data);
    } catch (error) {
      Alert.error(error.response?.data?.error || 'Failed to fetch payment data');
      navigate('/payment-history');
    } finally {
      setFetchingData(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'payment_amount') {
      if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
        setFormData(prev => ({ ...prev, [name]: value }));
        if (validationRules[name]) validateField(name, value, validationRules[name]);
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
      if (validationRules[name]) validateField(name, value, validationRules[name]);
    }
  };

  const calculateNewBalance = () => {
    const paymentAmount = parseFloat(formData.payment_amount || 0);
    const maxAllowed = shipmentDetails ? shipmentDetails.balance_amount + originalPayment.payment_amount : 0;
    
    // Use toFixed to avoid floating point precision issues
    return parseFloat((maxAllowed - paymentAmount).toFixed(2));
  };

  const isPaymentAmountExceeded = () => {
    const paymentAmount = parseFloat(formData.payment_amount || 0);
    const maxAllowed = shipmentDetails ? shipmentDetails.balance_amount + originalPayment.payment_amount : 0;
    return parseFloat(paymentAmount.toFixed(2)) > parseFloat(maxAllowed.toFixed(2));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate(formData, validationRules)) return;

    const paymentAmount = parseFloat(formData.payment_amount);

    const maxAllowed = shipmentDetails.balance_amount + originalPayment.payment_amount;
    if (parseFloat(paymentAmount.toFixed(2)) > parseFloat(maxAllowed.toFixed(2))) {
      Alert.error(`Payment amount cannot exceed ₹${maxAllowed.toFixed(2)}`);
      return;
    }

    const confirmed = await Alert.confirm(
      `Update payment amount to ₹${paymentAmount.toFixed(2)}?`,
      'Confirm Update'
    );

    if (!confirmed) return;

    try {
      setLoading(true);
      await paymentService.updatePayment(paymentId, formData);
      Alert.success('Payment updated successfully');
      navigate(`/shipments/view/${originalPayment.shipment_id}`);
    } catch (error) {
      Alert.error(error.response?.data?.error || 'Failed to update payment');
    } finally {
      setLoading(false);
    }
  };

  if (fetchingData) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20">
          <Loader fullScreen={false} size="default" message="Loading payment data..." />
        </div>
      </DashboardLayout>
    )
  }

  const maxAllowed = shipmentDetails ? shipmentDetails.balance_amount + originalPayment.payment_amount : 0;
  const newBalance = calculateNewBalance();
  const isAmountExceeded = isPaymentAmountExceeded();
  const isSubmitDisabled = loading || isAmountExceeded;

  return (
    <DashboardLayout>
              <div className="max-w-7xl mx-auto">
                {/* Header Section */}
                <div className="mb-6">

                  {/* Title + Back Button in one line */}
                  <div className="flex items-center justify-between w-full">
                    
                    {/* LEFT: Page Title */}
                    <h1 className="text-2xl sm:text-xl font-bold text-secondary flex items-center gap-2 sm:gap-3">
                      Edit Payment
                    </h1>

                    {/* RIGHT: Back Button */}
                    <Button variant="secondary" onClick={() => navigate(`/shipments/view/${originalPayment?.shipment_id}`)} icon={ArrowLeft}>
                      Back to Shipment
                    </Button>

                  </div>

                </div>

                {/* Payment Summary Card */}
                {shipmentDetails && originalPayment && (
                  <div className="bg-white rounded-xl p-6 mb-6 overflow-hidden relative">
                    <div className="absolute top-0 right-0 w-40 h-40 bg-blue-100 rounded-full -mr-20 -mt-20 opacity-50"></div>
                    <div className="relative">
                      <div className="items-center gap-2 mb-4">
                        <h2 className="text-base sm:text-md font-semibold text-secondary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">Payment Summary</h2>
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4">
                        <div>
                          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Shipment ID</p>
                          <p className="font-bold text-orange-600 font-mono text-lg">#{originalPayment.shipment_id}</p>
                        </div>
                        
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Current Balance</p>
                          </div>
                          <p className="font-bold text-red-700 text-lg">₹{shipmentDetails.balance_amount?.toFixed(2)}</p>
                        </div>
                        
                        <div>
                          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Original Payment</p>
                          <p className="font-bold text-gray-900 text-lg">₹{originalPayment.payment_amount?.toFixed(2)}</p>
                        </div>
                        
                        <div>
                          <div>
                            <p className="text-xs font-medium text-blue-700 uppercase tracking-wide">Maximum Allowed</p>
                          </div>
                          <p className="font-bold text-blue-700 text-lg">₹{maxAllowed.toFixed(2)}</p>
                        </div>
                      </div>

                      {/*<div className="mt-4 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-xl p-4 shadow-lg">
                        <div className="flex items-start gap-3 text-white">
                          <div className="bg-white/20 p-2 rounded-lg mt-0.5">
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                            </svg>
                          </div>
                          <div>
                            <p className="font-bold text-sm mb-1">Payment Update Information</p>
                            <p className="text-xs text-blue-50 leading-relaxed">
                              The maximum allowed amount includes the current balance (₹{shipmentDetails.balance_amount?.toFixed(2)}) plus the original payment amount (₹{originalPayment.payment_amount?.toFixed(2)}). This ensures you can adjust the payment without exceeding the shipment's total.
                            </p>
                          </div>
                        </div>
                      </div>*/}
                    </div>
                  </div>
                )}

                {/* Payment Form */}
                <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  <div className="px-6 py-4 pb-0">
                    <h3 className="text-base sm:text-md font-semibold text-secondary mb-0 sm:mb-0 pb-2 border-b-2 border-grey-400">
                      Update Payment Details
                    </h3>
                  </div>

                  <div className="p-6 grid grid-cols-2 md:grid-cols-2 gap-2 sm:gap-4">
                    {/* Payment Amount */}
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                        Payment Amount
                        <span className="text-red-500 ml-1">*</span>
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                          <span className="text-gray-500 font-semibold">₹</span>
                        </div>
                        <input
                          type="text"
                          name="payment_amount"
                          value={formData.payment_amount}
                          onChange={handleChange}
                          placeholder="0.00"
                          required
                          className={`w-full pl-9 pr-4 py-3 text-base border rounded-xl focus:outline-none transition-all font-semibold ${
                            (isAmountExceeded && formData.payment_amount) || errors.payment_amount
                              ? 'border-red-500 focus:ring-2 focus:ring-red-500 focus:border-transparent'
                              : 'border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent'
                          }`}
                        />
                      </div>
                      <ValidationMessage error={errors.payment_amount} />
                      <div className="flex items-center justify-between mt-2 text-xs">
                        <span className="text-gray-500">
                          Maximum: <span className="font-semibold text-gray-700">₹{maxAllowed.toFixed(2)}</span>
                        </span>
                        {formData.payment_amount && (
                          <>
                            {isAmountExceeded ? (
                              <span className="text-red-600 font-semibold bg-red-50 px-3 py-1 rounded-full flex items-center gap-1">
                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                                Amount Exceeds Maximum
                              </span>
                            ) : (
                              <span className="text-blue-600 font-semibold bg-blue-50 px-3 py-1 rounded-full">
                                New Balance: ₹{newBalance.toFixed(2)}
                              </span>
                            )}
                          </>
                        )}
                      </div>
                    </div>

                    {/* Payment Mode */}
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                        Payment Mode
                        <span className="text-red-500 ml-1">*</span>
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                          <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                          </svg>
                        </div>
                        <SelectField variant="form"
                          name="payment_mode"
                          value={formData.payment_mode}
                          onChange={handleChange}
                          required
                          className={`w-full pl-12 pr-4 py-3 text-base border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all appearance-none bg-white font-semibold ${errors.payment_mode ? 'border-red-400' : 'border-gray-300'}`}
                          style={{backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`, backgroundPosition: 'right 0.5rem center', backgroundRepeat: 'no-repeat', backgroundSize: '1.5em 1.5em'}}
                        >
                          {paymentModes.map(mode => (
                            <option key={mode.value} value={mode.value}>{mode.label}</option>
                          ))}
                        </SelectField>
                      </div>
                      <ValidationMessage error={errors.payment_mode} />
                    </div>

                    {/* Remarks */}
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                        Remarks
                        <span className="text-gray-400 text-xs ml-2 font-normal">(Optional)</span>
                      </label>
                      <div className="relative">
                        <textarea
                          name="remarks"
                          value={formData.remarks}
                          onChange={handleChange}
                          placeholder="Add any additional notes or comments about this payment..."
                          rows={4}
                          className="w-full px-4 py-3 text-base border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none transition-all"
                        />
                        <div className="absolute bottom-3 right-3 text-xs text-gray-400">
                          {formData.remarks.length} characters
                        </div>
                      </div>
                    </div>

                    {/* Existing Receipt */}
                    {existingReceipt && (
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Receipt
                        </label>
                          <a
                          href={existingReceipt}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition-colors text-sm font-medium"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                          View Receipt
                        </a>
                      </div>
                    )}
                  </div>

                  {/* Form Actions */}
                  <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex flex-col sm:flex-row gap-3">
                    <Button variant="primary" type="submit" disabled={isSubmitDisabled} loading={loading}>
                      {loading ? 'Updating Payment...' : 'Update Payment'}
                    </Button>
                    <Button variant="secondary" onClick={() => navigate(`/shipments/view/${originalPayment?.shipment_id}`)}>
                      Cancel
                    </Button>
                  </div>
                </form>
              </div>
    </DashboardLayout>
  );
}